// Vessel modifications (spec §8) with the documented magnitude from the research report's ship table.
import type { ModSpec } from '../types';
import { galToKg, DIESEL_KG_PER_L, GASOLINE_KG_PER_L } from '../physics/atmosphere';
import { SRC } from './sources';

/** Fuel bladders are per craft for the same reason. Mass is tracked but does not change the vessel model in v1. */
function fuelBladder(craftId: string, gal: number, kgPerL: number, massKg: number): ModSpec {
  return {
    id: `fuel-bladder-${craftId}`,
    name: 'Flexible fuel bladder',
    category: 'fuel-weight',
    domain: 'sea',
    appliesTo: [craftId],
    costCredits: 300,
    effect: { fuelCapacityAddKg: galToKg(gal, kgPerL), emptyMassAddKg: massKg },
    claim: `+${gal} US gal in a deck or bilge bladder (estimate) and +${massKg} kg of bladder and fittings; the added mass does not change the hull model in v1`,
    sources: [SRC.manPrimer],
  };
}

export const SEA_MODS: ModSpec[] = [
  {
    id: 'hull-cleaning',
    name: 'Hull cleaning',
    category: 'hull-airframe',
    domain: 'sea',
    appliesTo: ['*'],
    costCredits: 150,
    effect: { foulingReset: true },
    claim: 'Starts the run with a clean hull: removes the stock +9 % light-slime power penalty (Schultz 2011 DDG-51 table); ABS puts cleaning light slime at 7–9 %',
    sources: [SRC.schultz2011, SRC.absAdvisory],
  },
  {
    id: 'prop-polish',
    name: 'Propeller polishing',
    category: 'propulsor',
    domain: 'sea',
    appliesTo: ['*'],
    costCredits: 200,
    effect: { propulsivePowerMul: 0.97 },
    claim: '−3 % shaft power at a given speed (IMO GloFouling 3–8 %; ABS: 6 % from a Grade-F blade)',
    sources: [SRC.imoGlofouling, SRC.absAdvisory],
  },
  {
    id: 'premium-coating',
    name: 'Premium antifouling coating',
    category: 'hull-airframe',
    domain: 'sea',
    appliesTo: ['*'],
    costCredits: 600,
    effect: { foulingGrowthMul: 0.5 },
    claim: 'Halves the fouling growth rate in the model (estimate; CE Delft and IMO put coatings at 1–5 %, Hempel reports about 9 % on tankers after 48 months)',
    sources: [SRC.ceDelft, SRC.imoGlofouling, SRC.hempel],
  },
  {
    id: 'trim-optimisation',
    name: 'Trim optimisation',
    category: 'operations',
    domain: 'sea',
    appliesTo: ['*'],
    costCredits: 150,
    effect: { propulsivePowerMul: 0.985 },
    claim: '−1.5 % power (ABS: trim optimisation 1–2 %)',
    sources: [SRC.absAdvisory],
  },
  {
    id: 'large-slow-prop',
    name: 'Larger, slower-turning propeller',
    category: 'propulsor',
    domain: 'sea',
    appliesTo: ['trawler-40', 'destroyer-ddg', 'container-triple-e'],
    costCredits: 700,
    effect: { propulsivePowerMul: 0.96 },
    claim: '−4 % power at the design speed (MAN 80,000 dwt tanker: 7.2 → 7.4 m propeller −1.5 %; Kappel tips up to −6 %); the 3 % off-design penalty of a repitched propeller is not modelled in v1',
    sources: [SRC.manPrimer, SRC.absAdvisory],
  },
  {
    id: 'hotel-load-cut',
    name: 'Hotel-load cut',
    category: 'operations',
    domain: 'sea',
    appliesTo: ['*'],
    costCredits: 200,
    effect: { hotelMul: 0.7 },
    claim: '−30 % auxiliary load (estimate; the Maersk speed/fuel curve shows hotel load as a fixed addition on top of propulsion)',
    sources: [SRC.maerskEpa, SRC.absAdvisory],
  },
  {
    id: 'engine-tuning',
    name: 'Engine tuning and monitoring',
    category: 'engine',
    domain: 'sea',
    appliesTo: ['*'],
    costCredits: 250,
    effect: { bsfcMul: 0.985 },
    claim: '−1.5 % fuel per kWh (ABS: 1–2 % from tuning the engine)',
    sources: [SRC.absAdvisory],
  },
  {
    id: 'boss-cap-fins',
    name: 'Propeller boss-cap fins',
    category: 'propulsor',
    domain: 'sea',
    appliesTo: ['destroyer-ddg', 'container-triple-e'],
    costCredits: 400,
    effect: { propulsivePowerMul: 0.98 },
    claim: '−2 % delivered power (Wärtsilä: 2 % average, up to 5 % claimed)',
    sources: [SRC.wartsilaFin, SRC.absAdvisory],
  },
  {
    id: 'pre-swirl-stator',
    name: 'Pre-swirl stator',
    category: 'propulsor',
    domain: 'sea',
    appliesTo: ['container-triple-e'],
    costCredits: 900,
    effect: { propulsivePowerMul: 0.96 },
    claim: '−4 % delivered power (ABS 2–6 %; a bulk-carrier sea trial measured 6.8 %, Wärtsilä claims up to 10 %)',
    sources: [SRC.absAdvisory, SRC.iosStator, SRC.wartsilaStator],
  },
  fuelBladder('sailboat-34', 12, DIESEL_KG_PER_L, 5),
  fuelBladder('trawler-40', 100, DIESEL_KG_PER_L, 20),
  fuelBladder('console-26', 50, GASOLINE_KG_PER_L, 15),
];
